import { z } from "zod";

/**
 * How a tool's OAuth client authenticates to its token endpoint. THE one list:
 * the manifest schema below, `ExternalToolAuth`, the public v1 skill schema and
 * the backend token exchange all derive from it, so an unsupported value is
 * refused where the tool is written rather than failing after a user has already
 * been sent to the provider and back.
 *
 * - `post` — `client_id` and `client_secret` in the request body (RFC 6749 §2.3.1,
 *   `client_secret_post`). Also what an ABSENT value means.
 * - `basicAuthHeader` — HTTP Basic with the form-encoded client id and secret
 *   (RFC 6749 §2.3.1, `client_secret_basic`).
 */
export const TOKEN_EXCHANGE_METHODS = ["post", "basicAuthHeader"] as const;

export const TokenExchangeMethodSchema = z.enum(TOKEN_EXCHANGE_METHODS);

export type TokenExchangeMethod = z.infer<typeof TokenExchangeMethodSchema>;

/** The method an absent `tokenExchangeMethod` means. */
export const DEFAULT_TOKEN_EXCHANGE_METHOD: TokenExchangeMethod = "post";

/**
 * OAuth auth — Nexus manages the full OAuth flow using these credentials.
 * Client app belongs to Nexus (or the tool creator); tokens stored in ToolCredentials.
 *
 * All fields optional: marketplace tools may be partially configured,
 * and historical records may predate individual field additions.
 *
 * ⚠️ client_secret is sensitive — stored in plaintext in the tool record.
 * Existing architectural decision; encryption is a separate concern.
 */
export const CustomManifestOAuthSchema = z.object({
  type: z.literal("oauth"),
  grant_type: z.enum(["code", "pkce", "client_credentials", "refresh_token", "none"]).optional(),
  client_id: z.string().optional(),
  client_secret: z.string().optional(),
  client_url: z.string().optional(),
  authorization_url: z.string().optional(),
  scope: z.string().optional(),
  extra_body_params: z.record(z.string(), z.string()).optional(),
  tokenExchangeMethod: TokenExchangeMethodSchema.optional(),
  custom_header_name: z.string().optional(),
  custom_header_prefix: z.string().optional()
});

export type CustomManifestOAuth = z.infer<typeof CustomManifestOAuthSchema>;
